import { AxiosResponse } from 'axios';
import { proxyCreate, proxyDelete, proxyImport, proxyList, proxySpeedtest, proxyUpdate } from '@/api/proxy';
import { AppError } from '@/errors/appError';
import type { ProxyItem } from '@/models/proxy';
import { baseListService, isRequestCanceled } from '@/services/baseService';
import { IListParams } from '@/types';
import { IProxyApiListResponse, IProxyFilters, IProxyImportResponse, IProxySpeedtestResponse, IProxyUpdateParams, IProxyUpdateResponse } from '@/types/proxy';

export const proxyService = {
    async list(params: IListParams<IProxyFilters>): Promise<AxiosResponse<IProxyApiListResponse>> {
        try {
            return await baseListService<IProxyApiListResponse>(proxyList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Proxy API Error:', e);
            throw new AppError(`Unable to load proxy list, request error`, { cause: e });
        }
    },

    async save(item: ProxyItem): Promise<AxiosResponse<IProxyUpdateResponse>> {
        const row = item.toApiData();
        if (item.id) {
            return this.update({ rows: [row] });
        }
        return this.create({ rows: [row] });
    },

    async create(params: IProxyUpdateParams): Promise<AxiosResponse<IProxyUpdateResponse>> {
        try {
            return await proxyCreate(params);
        } catch (e) {
            console.error('Proxy create API Error:', e);
            throw new AppError(`Unable to create proxy, request error`, { cause: e });
        }
    },

    async update(params: IProxyUpdateParams): Promise<AxiosResponse<IProxyUpdateResponse>> {
        try {
            return await proxyUpdate(params);
        } catch (e) {
            console.error('Proxy update API Error:', e);
            throw new AppError(`Unable to update proxy, request error`, { cause: e });
        }
    },

    async delete(params: IProxyUpdateParams): Promise<AxiosResponse<IProxyUpdateResponse>> {
        try {
            return await proxyDelete(params);
        } catch (e) {
            console.error('Proxy delete API Error:', e);
            throw new AppError(`Unable to delete proxy, request error`, { cause: e });
        }
    },

    async deleteRows(ids: number[]): Promise<AxiosResponse<IProxyUpdateResponse>> {
        return this.delete({ rows: ids.map((id) => ({ id })) });
    },

    async speedtest(proxyId: number, siteIds: number[]): Promise<AxiosResponse<IProxySpeedtestResponse>> {
        try {
            return await proxySpeedtest({ id: proxyId, siteIds });
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Proxy speedtest API Error:', e);
            throw new AppError(`Unable to run proxy speedtest, request error`, { cause: e });
        }
    },

    async import(inputText: string): Promise<AxiosResponse<IProxyImportResponse>> {
        try {
            return await proxyImport(inputText);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('Proxy import API Error:', e);
            throw new AppError(`Unable to import proxies, request error`, { cause: e });
        }
    },
};
